export const FIELD_USES = [
  ["text", "Text someone enters"],
  ["company", "Company name"],
  ["saved", "Saved text"],
  ["date", "Date picker"],
  ["completed", "Date completed"],
  ["signature", "Signature"],
  ["signed", "Date signed"],
  ["checkbox", "Checkbox"],
];
export const fieldUse = (f) =>
  ({ company: "company", text: "saved", completion_date: "completed" })[
    f.prefill
  ] ||
  { signing_date: "signed" }[f.kind] ||
  f.kind;
export const emptyWorkflow = () => ({
  document_type: "official_form",
  treatment: "preserve",
  completion: "read_only",
  source_id: null,
  source_mode: "existing",
  blocks: [],
  fields: [],
  wording_edited: false,
  counter_sign: false,
  onboarding_roles: [],
  sop_template: false,
});
export const newField = (page, x = 10, y = 20) => ({
  id: "canvas_" + Math.random().toString(16).slice(2),
  kind: "text",
  label: "New field",
  page,
  x: Math.max(0, Math.min(65, x)),
  y: Math.max(0, Math.min(96, y)),
  width: 35,
  height: 4,
  required: false,
  read_only: false,
  signer: "employee",
  existing_name: null,
  options: [],
  prefill: "none",
  prefill_text: "",
});
// Keep all authored fields; skip suggestions that cover an existing answer area.
export function mergeDetectedFields(existing, suggestions) {
  const fields = existing.map(f => {
    let detected = suggestions.find(s => s.id === f.id && s.label === f.label &&
      ['page','x','y','width','height','existing_name'].every(k => s[k] === f[k]));
    const blank = f.label === 'New field' && f.kind === 'text' && !f.existing_name &&
      !f.read_only && !f.required && (!f.prefill || f.prefill === 'none') && f.signer === 'employee';
    if (!detected && blank) {
      const matches = suggestions.filter(s => {
        if (s.page !== f.page) return false;
        const width = Math.max(0,Math.min(f.x+f.width,s.x+s.width)-Math.max(f.x,s.x));
        const height = Math.max(0,Math.min(f.y+f.height,s.y+s.height)-Math.max(f.y,s.y));
        return width*height >= .65*s.width*s.height && width*height >= .3*f.width*f.height;
      });
      if (matches.length === 1) detected = matches[0];
    }
    if (detected && blank) return {...f,label:detected.label,kind:detected.kind,signer:detected.signer,required:detected.required};
    // Upgrade untouched detected text areas, retaining manual types, labels,
    // assignments, saved text and positions.
    return detected && f.kind === 'text' && !f.read_only &&
      (!f.prefill || f.prefill === 'none') && f.signer === 'employee' &&
      ['signature','signing_date','date'].includes(detected.kind)
      ? {...f, kind:detected.kind, signer:detected.signer, required:detected.required} : f;
  });
  for (const candidate of suggestions) {
    if (fields.some(f => f.id === candidate.id || (f.page === candidate.page &&
      Math.min(f.x+f.width,candidate.x+candidate.width) > Math.max(f.x,candidate.x)+.5 &&
      Math.min(f.y+f.height,candidate.y+candidate.height) > Math.max(f.y,candidate.y)+.5))) continue;
    if (fields.length >= 200) break;
    fields.push(candidate);
  }
  return fields;
}
const signing = (f) => ["signature", "signing_date"].includes(f.kind);
// Pair signatures with dates, reusing empty original widgets before adding boxes.
// Original widgets keep their coordinates and identity, including when unpaired.
export function normalizeWorkflow(workflow, preferredId) {
  let fields = workflow.fields.map((f) => ({ ...f }));
  const preferred = fields.find((f) => f.id === preferredId);
  for (const signer of ["employee", "general_manager"]) {
    const needsManager =
      signer === "general_manager" &&
      (fields.some((f) => f.signer === signer) ||
        workflow.onboarding_roles.length > 0);
    let sig = fields.find((f) => f.signer === signer && f.kind === "signature");
    const dt = fields.find(
      (f) => f.signer === signer && f.kind === "signing_date",
    );
    const pair = (kind, label, match) => {
      let candidate = fields.find(
        (f) =>
          f.kind === "text" &&
          !f.read_only &&
          (!f.prefill || f.prefill === "none") &&
          f.signer === signer &&
          (!sig || f.page === sig.page) &&
          match.test(f.label) &&
          f.id !== preferredId,
      );
      // A printed "Date" next to a signature belongs to that signer. Reuse
      // that empty area rather than placing another box at the page bottom.
      if (!candidate && kind === 'signing_date' && sig) {
        const nearby = fields.filter(f => f.kind === 'text' && !f.read_only &&
          (!f.prefill || f.prefill === 'none') && f.page === sig.page &&
          f.id !== preferredId && /^date\s*:?$/i.test(f.label) &&
          Math.abs(f.y+f.height/2-sig.y-sig.height/2) <= Math.max(1,Math.min(f.height,sig.height)/2) &&
          f.x >= sig.x+sig.width-1 && f.x-sig.x-sig.width <= 25);
        if (nearby.length === 1) candidate = nearby[0];
      }
      const result = candidate || {
        ...newField(
          sig?.page ?? preferred?.page ?? 0,
          signer === "employee" ? 8 : 54,
          kind === "signature" ? 80 : 86,
        ),
        width: 38,
      };
      Object.assign(result, {
        kind,
        label: candidate?.label || label,
        required: kind === "signature",
        prefill: "none",
        prefill_text: "",
        signer,
      });
      if (!candidate) fields.push(result);
      return result;
    };
    if (!sig && (dt || needsManager))
      sig = pair(
        "signature",
        signer === "employee"
          ? "Employee signature"
          : "General Manager signature",
        /^signature$|employee signature|manager signature/i,
      );
    if (sig && !dt)
      pair("signing_date", "Date signed", /date.*sign|sign.*date/i);
  }
  const employee = fields.filter(
    (f) => f.signer === "employee" && !f.read_only,
  );
  const hasSig = employee.some((f) => f.kind === "signature");
  const hasInput = employee.some(
    (f) => !signing(f) && (!f.prefill || f.prefill === "none"),
  );
  const counter =
    fields.some((f) => f.signer === "general_manager") ||
    workflow.onboarding_roles.length > 0;
  const completion = hasSig
    ? hasInput
      ? "fill_sign"
      : "sign"
    : hasInput
      ? "fill"
      : counter ||
          employee.some((f) => f.prefill === "completion_date") ||
          workflow.completion === "acknowledge"
        ? "acknowledge"
        : "read_only";
  return { ...workflow, fields, completion, counter_sign: counter };
}
export function patchCanvasField(wf, id, patch) {
  const previous = wf.fields.find((f) => f.id === id);
  let fields = wf.fields.map((f) => (f.id === id ? { ...f, ...patch } : f));
  // Removing or reassigning a signature must not leave a signing date with no signer.
  if (
    previous &&
    signing(previous) &&
    ((patch.kind && patch.kind !== previous.kind) ||
      (patch.signer && patch.signer !== previous.signer))
  ) {
    const otherKind =
      previous.kind === "signature" ? "signing_date" : "signature";
    if (
      !fields.some(
        (f) =>
          f.id !== id &&
          f.signer === previous.signer &&
          f.kind === previous.kind,
      )
    )
      fields = fields.flatMap((f) =>
        f.signer === previous.signer && f.kind === otherKind
          ? f.existing_name
            ? [{ ...f, kind: "text", required: false }]
            : []
          : [f],
      );
  }
  return normalizeWorkflow({ ...wf, fields }, id);
}
export function fieldUsePatch(f, use) {
  const base = {
    kind:
      use === "signed"
        ? "signing_date"
        : ["company", "saved", "completed"].includes(use)
          ? "text"
          : use,
    prefill: "none",
    prefill_text: "",
    required: use === "signature",
  };
  if (use === "company" || use === "saved")
    Object.assign(base, {
      prefill: use === "company" ? "company" : "text",
      prefill_text:
        f.prefill_text ||
        (use === "company" ? "Green Massage of Plantation" : ""),
    });
  if (use === "completed") base.prefill = "completion_date";
  return base;
}
export function removeCanvasField(wf, id) {
  const f = wf.fields.find((f) => f.id === id);
  if (!f || f.existing_name) return wf;
  let fields = wf.fields.filter((x) => x.id !== id);
  if (
    signing(f) &&
    !fields.some((x) => x.signer === f.signer && x.kind === f.kind)
  )
    fields = fields.flatMap((x) =>
      x.signer === f.signer && signing(x)
        ? x.existing_name
          ? [{ ...x, kind: "text", required: false }]
          : []
        : [x],
    );
  return normalizeWorkflow({ ...wf, fields });
}
export const editableValues = (fields, values, signer) =>
  Object.fromEntries(
    Object.entries(values).filter(([id]) =>
      fields.some(
        (f) =>
          f.id === id &&
          f.signer === signer &&
          !f.read_only &&
          (!f.prefill || f.prefill === "none") &&
          ["text", "date", "checkbox", "choice"].includes(f.kind),
      ),
    ),
  );
